// src/services/pollService.js - EXTREME DEBUG VERSION

import { supabase } from '../lib/supabase.js';

/**
 * Generate consistent anonymous identifier
 */
const generateAnonymousIdentifier = async () => {
  try {
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      navigator.platform
    ].join('|');
    
    const hashCode = fingerprint.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    const identifier = `fp_${Math.abs(hashCode)}`;
    sessionStorage.setItem('poll_identifier', identifier);
    
    console.log('🗳️ Generated poll identifier:', identifier);
    return identifier;
    
  } catch (error) {
    console.error('Error generating anonymous identifier:', error);
    let identifier = sessionStorage.getItem('poll_identifier');
    if (!identifier) {
      identifier = `session_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('poll_identifier', identifier);
    }
    return identifier;
  }
};

class PollService {
  constructor() {
    this.cache = new Map();
    this.userVotes = new Map();
    this.isOnline = true;
  }

  /**
   * EXTREME DEBUG: Get poll data with detailed logging
   */
  async getPollData(pollId) {
    if (!supabase || !this.isOnline) {
      console.log('❌ Supabase not available or offline');
      return null;
    }

    try {
      console.log('🔍 DEBUG: Starting getPollData for:', pollId);

      // Get poll details
      const { data: pollData, error: pollError } = await supabase
        .from('community_polls')
        .select('*')
        .eq('id', pollId)
        .eq('is_active', true)
        .single();

      console.log('🔍 DEBUG: Poll query result:', { pollData, pollError });

      if (pollError || !pollData) {
        console.error('❌ Error fetching poll:', pollError);
        return null;
      }

      // Get options with current vote counts
      const { data: optionsData, error: optionsError } = await supabase
        .from('community_poll_options')
        .select('*')
        .eq('poll_id', pollId)
        .order('display_order');

      console.log('🔍 DEBUG: Options query result:', { optionsData, optionsError });

      if (optionsError) {
        console.error('❌ Error fetching options:', optionsError);
        return null;
      }

      // EXTREME DEBUG: Check actual vote counts vs stored counts
      console.log('🔍 DEBUG: Checking actual vote counts...');
      for (const option of optionsData) {
        const { data: actualVotes, error: countError } = await supabase
          .from('anonymous_poll_votes')
          .select('id')
          .eq('poll_id', pollId)
          .eq('option_id', option.id);

        const actualCount = actualVotes?.length || 0;
        const storedCount = option.vote_count || 0;

        console.log(`🔍 DEBUG: Option "${option.option_text}":`, {
          storedCount,
          actualCount,
          match: storedCount === actualCount,
          optionId: option.id
        });

        if (countError) {
          console.error('❌ Error counting votes:', countError);
        }
      }

      // Transform to expected format
      const transformedPoll = {
        id: pollData.id,
        title: pollData.title,
        description: pollData.description,
        isActive: pollData.is_active,
        closesAt: pollData.closes_at,
        options: optionsData.map(option => ({
          id: option.id,
          text: option.option_text,
          emoji: option.emoji,
          description: option.description,
          votes: option.vote_count || 0,
          displayOrder: option.display_order
        })).sort((a, b) => a.displayOrder - b.displayOrder)
      };

      console.log('🔍 DEBUG: Transformed poll:', transformedPoll);
      this.cache.set(pollId, transformedPoll);
      
      return transformedPoll;

    } catch (error) {
      console.error('❌ Error in getPollData:', error);
      return null;
    }
  }

  /**
   * EXTREME DEBUG: Get user vote with detailed logging
   */
  async getUserVote(pollId) {
    if (!supabase || !this.isOnline) {
      console.log('❌ Supabase not available for getUserVote');
      return null;
    }

    try {
      const identifier = await generateAnonymousIdentifier();
      console.log('🔍 DEBUG: Getting user vote for identifier:', identifier);
      
      const { data, error } = await supabase
        .from('anonymous_poll_votes')
        .select('option_id, voted_at')
        .eq('poll_id', pollId)
        .eq('identifier', identifier)
        .single();

      console.log('🔍 DEBUG: User vote query result:', { data, error });

      if (error && error.code !== 'PGRST116') {
        console.error('❌ Error fetching user vote:', error);
        return null;
      }

      const userVote = data?.option_id || null;
      console.log('🔍 DEBUG: User vote determined:', userVote);

      if (userVote) {
        this.userVotes.set(pollId, userVote);
      }

      return userVote;

    } catch (error) {
      console.error('❌ Error in getUserVote:', error);
      return null;
    }
  }

  /**
   * EXTREME DEBUG: Vote with step-by-step logging
   */
  async vote(pollId, optionId) {
    console.log('🔍 DEBUG: Starting vote process:', { pollId, optionId });

    if (!supabase || !this.isOnline) {
      console.error('❌ Cannot vote: Supabase not available or offline');
      return { success: false, error: 'Cannot vote while offline' };
    }

    try {
      const identifier = await generateAnonymousIdentifier();
      console.log('🔍 DEBUG: Using identifier for vote:', identifier);
      
      // Check for existing vote
      console.log('🔍 DEBUG: Checking for existing vote...');
      const { data: existingVote, error: checkError } = await supabase
        .from('anonymous_poll_votes')
        .select('option_id, voted_at')
        .eq('poll_id', pollId)
        .eq('identifier', identifier)
        .single();

      console.log('🔍 DEBUG: Existing vote check result:', { existingVote, checkError });

      let result;
      let dbOperation = null;
      
      if (existingVote) {
        if (existingVote.option_id === optionId) {
          // Same option - remove vote (toggle off)
          console.log('🔍 DEBUG: Removing existing vote (toggle off)');
          dbOperation = 'remove';
          
          const { error } = await supabase
            .from('anonymous_poll_votes')
            .delete()
            .eq('poll_id', pollId)
            .eq('identifier', identifier);

          console.log('🔍 DEBUG: Remove vote result:', { error });

          if (error) {
            console.error('❌ Error removing vote:', error);
            return { success: false, error: error.message };
          }

          this.userVotes.set(pollId, null);
          result = { success: true, action: 'removed', optionId: null };
        } else {
          // Different option - update vote
          console.log('🔍 DEBUG: Changing vote from', existingVote.option_id, 'to', optionId);
          dbOperation = 'change';
          
          const { error } = await supabase
            .from('anonymous_poll_votes')
            .update({ 
              option_id: optionId,
              voted_at: new Date().toISOString()
            })
            .eq('poll_id', pollId)
            .eq('identifier', identifier);

          console.log('🔍 DEBUG: Change vote result:', { error });

          if (error) {
            console.error('❌ Error updating vote:', error);
            return { success: false, error: error.message };
          }

          this.userVotes.set(pollId, optionId);
          result = { success: true, action: 'changed', optionId: optionId };
        }
      } else {
        // New vote
        console.log('🔍 DEBUG: Adding new vote');
        dbOperation = 'add';
        
        const { error } = await supabase
          .from('anonymous_poll_votes')
          .insert({
            poll_id: pollId,
            option_id: optionId,
            identifier: identifier,
            identifier_type: 'fingerprint'
          });

        console.log('🔍 DEBUG: Add vote result:', { error });

        if (error) {
          console.error('❌ Error adding vote:', error);
          return { success: false, error: error.message };
        }

        this.userVotes.set(pollId, optionId);
        result = { success: true, action: 'added', optionId: optionId };
      }

      console.log('🔍 DEBUG: DB operation completed:', { dbOperation, result });
      
      // CRITICAL: Manually update vote counts
      console.log('🔍 DEBUG: Starting manual vote count update...');
      await this.updateAllVoteCounts(pollId);
      
      // Get fresh poll data
      console.log('🔍 DEBUG: Getting fresh poll data after vote...');
      const freshPollData = await this.getPollData(pollId);
      if (freshPollData) {
        result.pollData = freshPollData;
        console.log('🔍 DEBUG: Fresh poll data attached to result');
      } else {
        console.warn('⚠️ Could not get fresh poll data after vote');
      }
      
      console.log('🔍 DEBUG: Vote process completed successfully:', result);
      return result;

    } catch (error) {
      console.error('❌ Vote error:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  }

  /**
   * EXTREME DEBUG: Update vote counts with detailed logging
   */
  async updateAllVoteCounts(pollId) {
    console.log('🔍 DEBUG: Starting updateAllVoteCounts for:', pollId);
    
    try {
      // Get all options for this poll
      const { data: options, error: optionsError } = await supabase
        .from('community_poll_options')
        .select('id, option_text')
        .eq('poll_id', pollId);

      console.log('🔍 DEBUG: Options for count update:', { options, optionsError });

      if (optionsError) {
        console.error('❌ Error fetching options for count update:', optionsError);
        return;
      }

      // Update count for each option
      for (const option of options) {
        console.log(`🔍 DEBUG: Updating count for option: ${option.option_text}`);
        
        // Count actual votes for this option
        const { data: votes, error: countError } = await supabase
          .from('anonymous_poll_votes')
          .select('id')
          .eq('poll_id', pollId)
          .eq('option_id', option.id);

        console.log(`🔍 DEBUG: Vote count query for ${option.option_text}:`, { votes, countError });

        if (countError) {
          console.error('❌ Error counting votes for option:', option.id, countError);
          continue;
        }

        const actualCount = votes?.length || 0;
        console.log(`🔍 DEBUG: Option "${option.option_text}" has ${actualCount} actual votes`);

        // Update the vote_count in the options table
        const { error: updateError } = await supabase
          .from('community_poll_options')
          .update({ vote_count: actualCount })
          .eq('id', option.id);

        console.log(`🔍 DEBUG: Update result for ${option.option_text}:`, { updateError });

        if (updateError) {
          console.error('❌ Error updating vote count for option:', option.id, updateError);
        } else {
          console.log(`✅ Updated option ${option.id} vote_count to ${actualCount}`);
        }
      }

      console.log('✅ All vote counts updated successfully');

    } catch (error) {
      console.error('❌ Error in updateAllVoteCounts:', error);
    }
  }

  /**
   * Initialize service with extreme debugging
   */
  async initialize(pollId) {
    console.log('🔍 DEBUG: Initializing poll service with pollId:', pollId);

    try {
      if (!pollId) {
        console.error('❌ No poll ID provided to initialize');
        return null;
      }

      console.log('🔍 DEBUG: Getting poll data and user vote in parallel...');
      const [pollData, userVote] = await Promise.all([
        this.getPollData(pollId),
        this.getUserVote(pollId)
      ]);

      console.log('🔍 DEBUG: Initialize results:', { pollData: !!pollData, userVote });

      if (!pollData) {
        console.error('❌ Failed to initialize poll - no poll data');
        return null;
      }

      console.log('✅ Poll service initialized successfully');
      console.log('✅ Poll options:', pollData.options.map(o => ({
        text: o.text.substring(0, 20),
        votes: o.votes,
        id: o.id
      })));
      console.log('✅ User vote:', userVote);

      return { pollData, userVote };

    } catch (error) {
      console.error('❌ Error initializing poll service:', error);
      return null;
    }
  }

  /**
   * Clear cache for testing
   */
  clearCache() {
    this.cache.clear();
    this.userVotes.clear();
    console.log('🗳️ Poll service cache cleared');
  }
}

// Create singleton instance
const pollService = new PollService();

export default pollService;